import { useSyncExternalStore } from 'react';
import { EffectId } from './calendar';

/**
 * Estado compartido de la decoración: la cabecera y la barra lateral son dos
 * componentes distintos, pero un clic en uno cambia la variante en los dos, y un
 * efecto disparado desde la barra se pinta sobre toda la página. Un almacén mínimo
 * evita meter esto en un contexto de React que la app no tiene.
 */
interface SeasonState {
  variant: string;
  /** Cada disparo incrementa `effectSeq`; la capa de efectos reacciona al cambio. */
  effect: EffectId | null;
  effectSeq: number;
}

let state: SeasonState = { variant: 'default', effect: null, effectSeq: 0 };
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

export const setVariant = (variant: string) => {
  if (state.variant === variant) return;
  state = { ...state, variant };
  emit();
};

export const triggerEffect = (effect: EffectId) => {
  state = { ...state, effect, effectSeq: state.effectSeq + 1 };
  emit();
};

/**
 * Vuelve todo al inicio. Lo llama `useSeason` cuando el ID de la temporada resuelta
 * cambia (E07-F01, R3 / P8) y los `beforeEach` de las pruebas. Idempotente: si el estado
 * ya es el inicial no emite, porque `useSeason` vive en tres instancias y las tres
 * detectan el mismo cruce de temporada.
 */
export const resetSeasonState = () => {
  if (state.variant === 'default' && state.effect === null && state.effectSeq === 0) return;
  state = { variant: 'default', effect: null, effectSeq: 0 };
  emit();
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

export const useSeasonState = (): SeasonState => useSyncExternalStore(subscribe, () => state);
